// src/components/ErrorBoundary.jsx
//
// App-root React Error Boundary (app-error-boundary slice). Without a top-level
// boundary, a render/lifecycle error anywhere in the tree white-screens the
// user. This degrades that to a calm, on-brand "something went wrong" page.
//
// LIMITATION (React): an Error Boundary catches errors thrown during RENDER /
// lifecycle of its descendants only -- NOT event-handler or async (promise)
// errors. That is expected; this exists to kill the white-screen on render
// crashes, not to be a global try/catch.
//
// It sits ABOVE the Router (wraps <App/> in main.jsx), so the fallback cannot
// use react-router <Link>; the Home link is a plain <a href="/"> (a full
// navigation, which also remounts the app and clears the crashed tree).
import React from 'react';

export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // TODO: when error monitoring is added (ops), report here.
        console.error('App crash:', error, info);
    }

    render() {
        if (!this.state.hasError) return this.props.children;

        const devMessage = import.meta.env.DEV && this.state.error && this.state.error.message;

        return (
            <div className="min-h-screen bg-book-bg paper-texture flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md text-center border border-book-accent/30 bg-book-page/60 p-10 decorative-corner text-book-accent">
                    <div className="ornamental-divider mb-6 text-book-accent">
                        <span className="font-display text-xs font-semibold text-book-muted">We're sorry</span>
                    </div>
                    <h1 className="font-display text-book-text text-2xl md:text-3xl mb-4">Something went wrong</h1>
                    <p className="font-serif italic text-book-muted leading-relaxed mb-8">
                        An unexpected error occurred. Please reload the page.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button onClick={() => window.location.reload()}
                            className="px-5 py-3 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors font-semibold">
                            Reload
                        </button>
                        <a href="/"
                            className="px-5 py-3 border border-book-accent/40 text-book-accent font-display text-xs hover:bg-book-accent/10 transition-colors inline-flex items-center justify-center font-semibold">
                            Go to Home
                        </a>
                    </div>
                    {devMessage && (
                        <p className="font-serif text-book-muted/70 text-xs mt-6 break-words">{devMessage}</p>
                    )}
                </div>
            </div>
        );
    }
}
